import {
  ContentCopy,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import API_ENDPOINTS from '../../config/api';

const FONT_FAMILY =
  'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

const SECTION_SX = {
  bgcolor: '#FFFFFF',
  borderRadius: '20px',
  filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.15))',
  px: 2.5,
  py: 2.5,
  mb: 2,
};

const SECTION_HEADING_SX = {
  fontFamily: FONT_FAMILY,
  fontWeight: 700,
  fontSize: 18,
  color: '#000000',
  mb: 2,
};

interface AccountSectionProps {
  userId: string;
  subscriptionProviderName: string;
  activeMembership?: {
    provider: {
      business_name: string;
    };
    site: {
      name: string;
    };
  } | null;
  sameProviderCooldownSeconds?: number;
  leavingProvider: boolean;
  onLogout: () => void;
  onLeaveProvider: () => Promise<boolean>;
}

const AccountSection: React.FC<AccountSectionProps> = ({
  userId,
  subscriptionProviderName,
  activeMembership,
  sameProviderCooldownSeconds = 0,
  leavingProvider,
  onLogout,
  onLeaveProvider,
}) => {
  const [copiedId, setCopiedId] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

  const formatDuration = (seconds: number): string => {
    if (seconds <= 0) {
      return 'no cooldown period';
    }
    const hours = Math.round(seconds / 3600);
    if (hours >= 24 && hours % 24 === 0) {
      const days = hours / 24;
      return `${days} ${days === 1 ? 'day' : 'days'}`;
    }
    if (hours >= 1) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    const minutes = Math.max(1, Math.round(seconds / 60));
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  };

  const cooldownText = formatDuration(sameProviderCooldownSeconds);

  const handleCopyUserId = async (): Promise<void> => {
    if (!userId) return;
    try {
      await navigator.clipboard.writeText(userId);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch {
      // Reason: fallback for older browsers / mobile WebView
      const textArea = document.createElement('textarea');
      textArea.value = userId;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  const handleConfirmLeave = async (): Promise<void> => {
    const leftProvider = await onLeaveProvider();
    if (leftProvider) {
      setLeaveDialogOpen(false);
    }
  };

  return (
    <Box sx={SECTION_SX}>
      <Typography sx={SECTION_HEADING_SX}>Account</Typography>

      <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 13,
              color: '#828282',
            }}
          >
            Subscription Provider
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            {subscriptionProviderName || 'Not specified'}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 13,
              color: '#828282',
            }}
          >
            User ID
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: 15,
                fontWeight: 600,
                overflowWrap: 'break-word',
                wordBreak: 'keep-all',
                maxWidth: '200px',
              }}
            >
              {userId}
            </Typography>
            <Tooltip
              title={copiedId ? 'Copied!' : 'Copy to clipboard'}
              arrow
            >
              <IconButton
                size="small"
                onClick={handleCopyUserId}
                sx={{ ml: 0.5, p: 0.5 }}
              >
                <ContentCopy
                  sx={{
                    fontSize: 16,
                    color: copiedId ? '#FFD400' : '#828282',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Buttons with vertical spacing for mobile stacking */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
        <Button
          variant="contained"
          component="a"
          href="https://jeffsays.com/terms-of-use.html"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            bgcolor: '#FFD400',
            color: '#000000',
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            borderRadius: '12px',
            px: 3,
            py: 1,
            textTransform: 'none',
            '&:hover': { bgcolor: '#E6BF00' },
          }}
        >
          Terms of Use
        </Button>

        <Button
          variant="contained"
          component="a"
          href="https://jeffsays.com/privacy-policy.html"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            bgcolor: '#FFD400',
            color: '#000000',
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            borderRadius: '12px',
            px: 3,
            py: 1,
            textTransform: 'none',
            '&:hover': { bgcolor: '#E6BF00' },
          }}
        >
          Privacy Policy
        </Button>

        <Button
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={onLogout}
          sx={{
            color: '#E64646',
            borderColor: '#E64646',
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            borderRadius: '12px',
            px: 3,
            py: 1,
            textTransform: 'none',
            '&:hover': {
              borderColor: '#CC0000',
              bgcolor: 'rgba(230, 70, 70, 0.05)',
            },
          }}
        >
          Logout
        </Button>

        {activeMembership && (
          <>
            <Button
              variant="contained"
              component="a"
              href="https://jeffsays.com/provider-terms.html"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: '#9ED8FF',
                color: '#000000',
                fontFamily: FONT_FAMILY,
                fontWeight: 700,
                borderRadius: '12px',
                px: 3,
                py: 1,
                textTransform: 'none',
                '&:hover': { bgcolor: '#7BCBFF' },
              }}
            >
              Provider Terms of Use
            </Button>
            <Button
              variant="contained"
              component="a"
              href="https://jeffsays.com/provider-privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: '#9ED8FF',
                color: '#000000',
                fontFamily: FONT_FAMILY,
                fontWeight: 700,
                borderRadius: '12px',
                px: 3,
                py: 1,
                textTransform: 'none',
                '&:hover': { bgcolor: '#7BCBFF' },
              }}
            >
              Provider Privacy Policy
            </Button>
            <Button
              variant="contained"
              onClick={() => setLeaveDialogOpen(true)}
              sx={{
                bgcolor: '#9ED8FF',
                color: '#000000',
                fontFamily: FONT_FAMILY,
                fontWeight: 700,
                borderRadius: '12px',
                px: 3,
                py: 1,
                textTransform: 'none',
                '&:hover': { bgcolor: '#7BCBFF' },
              }}
            >
              Leave Provider
            </Button>
          </>
        )}
      </Box>

      <Dialog
        open={leaveDialogOpen}
        onClose={() => setLeaveDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>
          Leave provider?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT_FAMILY, mb: 2 }}>
            You are about to leave {activeMembership?.provider.business_name} /
            {' '}{activeMembership?.site.name}.
          </Typography>
          <Typography sx={{ fontFamily: FONT_FAMILY, color: '#666', mb: 1.5 }}>
            You will need to wait {cooldownText} before you are able to accept
            another Provider invite.
          </Typography>
          <Typography sx={{ fontFamily: FONT_FAMILY, color: '#666' }}>
            Leaving a Provider does NOT affect your access to the Jeff Says app
            - only the Provider features.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setLeaveDialogOpen(false)}
            disabled={leavingProvider}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmLeave}
            disabled={leavingProvider}
            sx={{
              bgcolor: '#9ED8FF',
              color: '#000000',
              '&:hover': { bgcolor: '#7BCBFF' },
            }}
          >
            {leavingProvider ? 'Leaving…' : 'Leave Provider'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountSection;
